"use client";

import { PortableText } from "next-sanity";
import { usePathname } from "next/navigation";
import NextImage from "next/image";

import IQCLogo from "../global/ui/IQCLogo";
import { CTAButton } from "@/shadcn/ui/button";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PageThankYouSchema } from "@/sanity/pages/PageThankYouTemplate";
import {
  resolveI18nText,
  NON_DEFAULT_LANGUAGES,
  DEFAULT_LANGUAGE,
} from "@/sanity/globals/I18n";
import { resolvePageLink } from "@/sanity/globals/CTA";

import bgTexture from "@/public/thank-you/bg-texture.png";
import statue from "@/public/thank-you/statue.webp";
import chevronDown from "@/public/thank-you/chevron-down.svg";

/** Stacked chevrons darken downwards, pointing at the voice-message card. */
const CHEVRON_OPACITIES = [0.2, 0.5, 1];

export default function PageThankYouJSX({
  data,
}: {
  data: PageThankYouSchema;
}) {
  const pathname = usePathname();
  const language =
    NON_DEFAULT_LANGUAGES.find(
      (l) => pathname.startsWith(`/${l}/`) || pathname === `/${l}`,
    ) ?? DEFAULT_LANGUAGE;

  const footer = data.settings?.footer;
  const legalLinks =
    footer?.navColumns?.find(
      (col) =>
        resolveI18nText(col.heading, col.i18n, language)?.toUpperCase() ===
        "POLICY",
    )?.links ?? [];

  const questions = data.voiceQuestions ?? [];

  return (
    <div className="relative flex min-h-screen w-full flex-col overflow-x-clip bg-white">
      {/* Ornate background texture, faded into white top and bottom */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[135rem] overflow-hidden"
      >
        <NextImage
          src={bgTexture}
          alt=""
          fill
          priority
          className="object-cover object-bottom opacity-35"
        />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(180deg, #ffffff 0%, rgba(255,255,255,0) 140.47%), linear-gradient(0deg, #ffffff 20.421%, rgba(255,255,255,0) 66.449%)",
          }}
        />
      </div>

      {/* Header — logo only */}
      <header className="relative z-10 flex w-full items-center justify-center border-b border-black/10 px-[3rem] py-[2.25rem]">
        <IQCLogo fill="#001b14" width="17rem" height="3.25rem" />
      </header>

      {/* Main */}
      <main className="relative z-10 flex flex-1 flex-col items-center pt-[8rem] lg:pt-[5.25rem]">
        <div className="flex w-[min(110.5rem,100%)] flex-col items-center gap-[2.25rem] px-[2rem]">
          <div className="flex w-full flex-col items-center gap-[0.5rem]">
            {/* Title + description */}
            <div className="flex w-full flex-col items-center gap-[1.75rem]">
              {data.heroTitle && (
                <div
                  className="richtext richtext-center prose-headings:mb-[1rem] prose-headings:font-geist prose-headings:text-[5rem] lg:prose-headings:text-[6.75rem] prose-headings:font-semibold prose-headings:leading-[1.15] prose-headings:tracking-[-0.27rem] prose-headings:text-black
                  prose-p:text-center prose-p:font-geist prose-p:text-[2.25rem] prose-p:font-normal prose-p:leading-[1.33] prose-p:text-black/70"
                >
                  <PortableText
                    value={data.heroTitle}
                    components={richTextComponent}
                  />
                </div>
              )}
            </div>

            {/* Chevron stack */}
            <div aria-hidden className="flex flex-col items-start">
              {CHEVRON_OPACITIES.map((opacity, i) => (
                <NextImage
                  key={opacity}
                  src={chevronDown}
                  alt=""
                  width={24}
                  height={24}
                  style={{ opacity }}
                  className={`h-[3rem] w-[3rem] shrink-0 ${i < CHEVRON_OPACITIES.length - 1 ? "mb-[-1.625rem]" : ""}`}
                />
              ))}
            </div>

            {/* Voice message card */}
            {(data.voiceTitle || !!questions.length) && (
              <div className="flex flex-col items-center justify-center gap-[1.5rem] rounded-[0.5rem] bg-[#f6f6f6] px-[1.25rem] pb-[1.25rem] pt-[1.75rem]">
                {data.voiceTitle && (
                  <p className="mb-0 text-center font-geist text-[2.25rem] font-normal leading-[1.33] text-black">
                    {data.voiceTitle}
                  </p>
                )}
                {!!questions.length && (
                  <ul
                    className="flex w-full flex-col justify-center gap-[0.625rem]"
                    style={{ textWrapStyle: "pretty" }}
                  >
                    {questions.map((question, i) => (
                      <li
                        key={question._key ?? i}
                        className="flex items-start gap-[1rem] border border-black/[0.04] bg-white px-[2.25rem] py-[1.5rem] text-[2.25rem] leading-[1.33]"
                      >
                        <span className="shrink-0 font-geist font-medium tracking-[-0.0675rem] text-[#228e71]">
                          {String(i + 1).padStart(2, "0")}.
                        </span>
                        <span className="font-geist font-normal text-black/70">
                          {question.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* CTA */}
          {(data.ctaNote || data.ctaButton) && (
            <div className="flex w-[min(52.375rem,100%)] flex-col items-center gap-[2.75rem]">
              {data.ctaNote && (
                <p className="mb-0 text-center font-geist text-[2.25rem] font-normal leading-[1.44] text-black">
                  {data.ctaNote}
                </p>
              )}
              {data.ctaButton && (
                <div className="min-h-[7rem] [&_button]:h-full w-full">
                  <CTAButton data={data.ctaButton} className="h-full" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Statue, blurred into white at the bottom */}
        <div className="relative -mt-[1rem] w-full lg:-mt-[4rem]">
          <div className="relative mx-auto !w-[min(89.0625rem,100%)]">
            <NextImage
              src={statue}
              alt=""
              className="!w-full !h-full object-cover -translate-x-[3rem] lg:-translate-x-[10rem]"
            />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 -mt-[8rem] w-full px-[3rem] py-[2.75rem] lg:-mt-[11.625rem] lg:px-[4rem]">
        <div className="mx-auto flex w-[min(160rem,100%)] flex-col items-center justify-between gap-[1rem] text-center font-geist text-[2rem] font-normal leading-[1.5] text-black lg:flex-row lg:text-left">
          <p className="mb-0 opacity-50">© IQ Capital - All rights reserved</p>
          {!!legalLinks.length && (
            <ul className="m-0 flex list-none flex-wrap items-center justify-center gap-x-[1rem] gap-y-[0.5rem] p-0 lg:justify-end">
              {legalLinks.map((item, idx) => {
                const href = resolvePageLink(item.link, language) ?? "#";
                const isExternal =
                  item.link?.externalLink && item.link?.openInNewTab;
                const isLast = idx === legalLinks.length - 1;
                return (
                  <li key={idx} className="m-0 flex items-center gap-[1rem]">
                    <a
                      href={href}
                      target={isExternal ? "_blank" : undefined}
                      rel={isExternal ? "noopener noreferrer" : undefined}
                      className="underline-offset-4 opacity-50 transition-all hover:underline"
                    >
                      {resolveI18nText(item.title, item.i18n, language)}
                    </a>
                    {!isLast && <span className="opacity-30">/</span>}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </footer>
    </div>
  );
}
